import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { Card } from '@mdvip/shared/components/ui/card';
import type * as React from 'react';

interface PaymentAccountSectionProps {
  heading: string;
  addLabel: string;
  onAdd: () => void;
  disclaimer?: string;
  isLoading?: boolean;
  isEmpty: boolean;
  emptyIcon: IconName;
  emptyHeading: string;
  emptyBody: string;
  children: React.ReactNode;
}

function SkeletonRow() {
  return (
    <Card className="flex-row! animate-pulse items-center gap-3! rounded-xl border-border py-0! pr-2 shadow-sm">
      <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5">
        <div className="h-8 w-12 shrink-0 rounded-md bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-32 rounded bg-muted" />
          <div className="h-3 w-24 rounded bg-muted" />
        </div>
      </div>
    </Card>
  );
}

export function PaymentAccountSection({
  heading,
  addLabel,
  onAdd,
  disclaimer,
  isLoading = false,
  isEmpty,
  emptyIcon,
  emptyHeading,
  emptyBody,
  children,
}: PaymentAccountSectionProps) {
  return (
    <div className="grid grid-cols-4 gap-4 md:grid-cols-8 md:gap-6 lg:grid-cols-12 lg:gap-6">
      {/* Left sidebar */}
      <div className="col-span-4 flex flex-col md:col-span-8 lg:col-span-4">
        <h3 className="mb-3 text-h3 font-semibold text-foreground">{heading}</h3>
        {disclaimer && <p className="text-xs leading-relaxed text-muted-foreground">{disclaimer}</p>}
        <Button
          variant="default"
          size="sm"
          onClick={onAdd}
          className="mt-4 w-fit lg:h-12 lg:px-6 lg:text-base lg:leading-6"
        >
          {addLabel}
        </Button>
      </div>

      {/* Right column */}
      <div className="col-span-4 md:col-span-8 lg:col-span-8">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 px-6 py-12 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Icon name={emptyIcon} className="text-2xl" />
            </div>
            <p className="text-sm font-semibold text-foreground">{emptyHeading}</p>
            <p className="mt-1 max-w-xs text-xs leading-relaxed text-muted-foreground">{emptyBody}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">{children}</div>
        )}
      </div>
    </div>
  );
}
